import "../../../src/components/VTextField/VTextField.sass";
import "../../../src/components/VSelect/VSelect.sass";
import VChip from '../VChip';
import VMenu from '../VMenu';
import VSelectList from './VSelectList';
import VInput from '../VInput';
import VTextField from '../VTextField/VTextField';
import Comparable from '../../mixins/comparable';
import Dependent from '../../mixins/dependent';
import Filterable from '../../mixins/filterable';
import ClickOutside from '../../directives/click-outside';
import mergeData from '../../util/mergeData';
import { getPropertyFromItem, getObjectValueByPath, keyCodes } from '../../util/helpers';
import { consoleError } from '../../util/console';
import mixins from '../../util/mixins';
export const defaultMenuProps = {
    closeOnClick: false,
    closeOnContentClick: false,
    disableKeys: true,
    openOnClick: false,
    maxHeight: 304
};
const baseMixins = mixins(VTextField, Comparable, Dependent, Filterable);
export default baseMixins.extend().extend({
    name: 'v-select',
    directives: {
        ClickOutside
    },
    props: {
        appendIcon: {
            type: String,
            default: '$dropdown'
        },
        attach: {
            type: null,
            default: false
        },
        cacheItems: Boolean,
        chips: Boolean,
        clearable: Boolean,
        deletableChips: Boolean,
        disableLookup: Boolean,
        eager: Boolean,
        hideSelected: Boolean,
        items: {
            type: Array,
            default: () => []
        },
        itemColor: {
            type: String,
            default: 'primary'
        },
        itemDisabled: {
            type: [String, Array, Function],
            default: 'disabled'
        },
        itemText: {
            type: [String, Array, Function],
            default: 'text'
        },
        itemValue: {
            type: [String, Array, Function],
            default: 'value'
        },
        menuProps: {
            type: [String, Array, Object],
            default: () => defaultMenuProps
        },
        multiple: Boolean,
        openOnClear: Boolean,
        returnObject: Boolean,
        smallChips: Boolean
    },
    data() {
        return {
            cachedItems: this.cacheItems ? this.items : [],
            menuIsBooted: false,
            isMenuActive: false,
            lastItem: 20,
            lazyValue: this.value !== undefined ? this.value : this.multiple ? [] : undefined,
            selectedIndex: -1,
            selectedItems: [],
            keyboardLookupPrefix: '',
            keyboardLookupLastTime: 0
        };
    },
    computed: {
        allItems() {
            return this.filterDuplicates(this.cachedItems.concat(this.items));
        },
        classes() {
            return { ...VTextField.options.computed.classes.call(this),
                'v-select': true,
                'v-select--chips': this.hasChips,
                'v-select--chips--small': this.smallChips,
                'v-select--is-menu-active': this.isMenuActive,
                'v-select--is-multi': this.multiple
            };
        },
        computedItems() {
            return this.allItems;
        },
        computedOwns() {
            return `list-${this._uid}`;
        },
        computedCounterValue() {
            const value = this.multiple ? this.selectedItems : (this.getText(this.selectedItems[0]) || '').toString();
            if (typeof this.counterValue === 'function') {
                return this.counterValue(value);
            }
            return value.length;
        },
        directives() {
            return this.isFocused ? [{
                    name: 'click-outside',
                    value: {
                        handler: this.blur,
                        closeConditional: this.closeConditional,
                        include: () => this.getOpenDependentElements()
                    }
                }] : undefined;
        },
        dynamicHeight() {
            return 'auto';
        },
        hasChips() {
            return this.chips || this.smallChips;
        },
        hasSlot() {
            return Boolean(this.hasChips || this.$scopedSlots.selection);
        },
        isDirty() {
            return this.selectedItems.length > 0;
        },
        listData() {
            const scopeId = this.$vnode && this.$vnode.context.$options._scopeId;
            const attrs = scopeId ? {
                [scopeId]: true
            } : {};
            return {
                attrs: { ...attrs,
                    id: this.computedOwns
                },
                props: {
                    action: this.multiple,
                    color: this.itemColor,
                    dense: this.dense,
                    hideSelected: this.hideSelected,
                    items: this.virtualizedItems,
                    itemDisabled: this.itemDisabled,
                    itemText: this.itemText,
                    itemValue: this.itemValue,
                    noDataText: this.$vuetify.lang.t(this.noDataText),
                    selectedItems: this.selectedItems
                },
                on: {
                    select: this.selectItem
                },
                scopedSlots: {
                    item: this.$scopedSlots.item
                }
            };
        },
        staticList() {
            if (this.$slots['no-data'] || this.$slots['prepend-item'] || this.$slots['append-item']) {
                consoleError('assert: staticList should not be called if slots are used');
            }
            return this.$createElement(VSelectList, this.listData);
        },
        virtualizedItems() {
            return this.$_menuProps.auto ? this.computedItems : this.computedItems.slice(0, this.lastItem);
        },
        menuCanShow: () => true,
        $_menuProps() {
            let normalisedProps = typeof this.menuProps === 'string' ? this.menuProps.split(',') : this.menuProps;
            if (Array.isArray(normalisedProps)) {
                normalisedProps = normalisedProps.reduce((acc, p) => {
                    acc[p.trim()] = true;
                    return acc;
                }, {});
            }
            return { ...defaultMenuProps,
                eager: this.eager,
                value: this.menuCanShow && this.isMenuActive,
                nudgeBottom: normalisedProps.offsetY ? 1 : 0,
                ...normalisedProps
            };
        }
    },
    watch: {
        internalValue(val) {
            this.initialValue = val;
            this.setSelectedItems();
        },
        isMenuActive(val) {
            window.setTimeout(() => this.onMenuActiveChange(val));
        },
        items: {
            immediate: true,
            handler(val) {
                if (this.cacheItems) {
                    this.$nextTick(() => {
                        this.cachedItems = this.filterDuplicates(this.cachedItems.concat(val));
                    });
                }
                this.setSelectedItems();
            }
        }
    },
    methods: {
        blur(e) {
            VTextField.options.methods.blur.call(this, e);
            this.isMenuActive = false;
            this.isFocused = false;
            this.selectedIndex = -1;
            this.setMenuIndex(-1);
        },
        activateMenu() {
            if (!this.isInteractive || this.isMenuActive)
                return;
            this.isMenuActive = true;
        },
        clearableCallback() {
            this.setValue(this.multiple ? [] : null);
            this.setMenuIndex(-1);
            this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
            if (this.openOnClear)
                this.isMenuActive = true;
        },
        closeConditional(e) {
            if (!this.isMenuActive)
                return true;
            return !this._isDestroyed && (!this.getContent() || !this.getContent().contains(e.target)) &&
                this.$el && !this.$el.contains(e.target) && e.target !== this.$el;
        },
        filterDuplicates(arr) {
            const uniqueValues = new Map();
            for (let index = 0; index < arr.length; ++index) {
                const item = arr[index];
                if (item == null) {
                    continue;
                }
                if (item.header || item.divider) {
                    uniqueValues.set(item, item);
                    continue;
                }
                const val = this.getValue(item);
                !uniqueValues.has(val) && uniqueValues.set(val, item);
            }
            return Array.from(uniqueValues.values());
        },
        findExistingIndex(item) {
            const itemValue = this.getValue(item);
            return (this.internalValue || []).findIndex(i => this.valueComparator(this.getValue(i), itemValue));
        },
        getContent() {
            return this.$refs.menu && this.$refs.menu.$refs.content;
        },
        genChipSelection(item, index) {
            const isDisabled = this.isDisabled || this.getDisabled(item);
            const isInteractive = !isDisabled && this.isInteractive;
            return this.$createElement(VChip, {
                staticClass: 'v-chip--select',
                attrs: {
                    tabindex: -1
                },
                props: {
                    close: this.deletableChips && isInteractive,
                    disabled: isDisabled,
                    inputValue: index === this.selectedIndex,
                    small: this.smallChips
                },
                on: {
                    click: e => {
                        if (!isInteractive)
                            return;
                        e.stopPropagation();
                        this.selectedIndex = index;
                    },
                    'click:close': () => this.onChipInput(item)
                },
                key: JSON.stringify(this.getValue(item))
            }, this.getText(item));
        },
        genCommaSelection(item, index, last) {
            const color = index === this.selectedIndex && this.computedColor;
            const isDisabled = this.isDisabled || this.getDisabled(item);
            return this.$createElement('div', this.setTextColor(color, {
                staticClass: 'v-select__selection v-select__selection--comma',
                class: {
                    'v-select__selection--disabled': isDisabled
                },
                key: JSON.stringify(this.getValue(item))
            }), `${this.getText(item)}${last ? '' : ', '}`);
        },
        genDefaultSlot() {
            const selections = this.genSelections();
            const input = this.genInput();
            if (Array.isArray(selections)) {
                selections.push(input);
            }
            else {
                selections.children = selections.children || [];
                selections.children.push(input);
            }
            return [this.genFieldset(), this.$createElement('div', {
                    staticClass: 'v-select__slot',
                    directives: this.directives
                }, [this.genLabel(), this.prefix ? this.genAffix('prefix') : null, selections, this.suffix ? this.genAffix('suffix') : null, this.genClearIcon(), this.genIconSlot(), this.genHiddenInput()]), this.genMenu(), this.genProgress()];
        },
        genIcon(type, cb, extraData) {
            const icon = VInput.options.methods.genIcon.call(this, type, cb, extraData);
            if (type === 'append') {
                icon.children[0].data = mergeData(icon.children[0].data, {
                    attrs: {
                        tabindex: icon.children[0].componentOptions.listeners && '-1',
                        'aria-hidden': 'true',
                        'aria-label': undefined
                    }
                });
            }
            return icon;
        },
        genInput() {
            const input = VTextField.options.methods.genInput.call(this);
            delete input.data.attrs.name;
            input.data = mergeData(input.data, {
                domProps: {
                    value: null
                },
                attrs: {
                    readonly: true,
                    type: 'text',
                    'aria-readonly': String(this.isReadonly),
                    'aria-activedescendant': getObjectValueByPath(this.$refs.menu, 'activeTile.id'),
                    autocomplete: getObjectValueByPath(input.data, 'attrs.autocomplete', 'off'),
                    placeholder: !this.isDirty && (this.persistentPlaceholder || this.isFocused || !this.hasLabel) ? this.placeholder : undefined
                },
                on: {
                    keypress: this.onKeyPress
                }
            });
            return input;
        },
        genHiddenInput() {
            return this.$createElement('input', {
                domProps: {
                    value: this.lazyValue
                },
                attrs: {
                    type: 'hidden',
                    name: this.attrs$.name
                }
            });
        },
        genInputSlot() {
            const render = VTextField.options.methods.genInputSlot.call(this);
            render.data.attrs = { ...render.data.attrs,
                role: 'button',
                'aria-haspopup': 'listbox',
                'aria-expanded': String(this.isMenuActive),
                'aria-owns': this.computedOwns
            };
            return render;
        },
        genList() {
            if (this.$slots['no-data'] || this.$slots['prepend-item'] || this.$slots['append-item']) {
                return this.genListWithSlot();
            }
            else {
                return this.staticList;
            }
        },
        genListWithSlot() {
            const slots = ['prepend-item', 'no-data', 'append-item'].filter(slotName => this.$slots[slotName]).map(slotName => this.$createElement('template', {
                slot: slotName
            }, this.$slots[slotName]));
            return this.$createElement(VSelectList, { ...this.listData
            }, slots);
        },
        genMenu() {
            const props = this.$_menuProps;
            props.activator = this.$refs['input-slot'];
            if (this.attach === '' ||
                this.attach === true ||
                this.attach === 'attach') {
                props.attach = this.$el;
            }
            else {
                props.attach = this.attach;
            }
            return this.$createElement(VMenu, {
                attrs: {
                    role: undefined
                },
                props,
                on: {
                    input: val => {
                        this.isMenuActive = val;
                        this.isFocused = val;
                    },
                    scroll: this.onScroll
                },
                ref: 'menu'
            }, [this.genList()]);
        },
        genSelections() {
            let length = this.selectedItems.length;
            const children = new Array(length);
            let genSelection;
            if (this.$scopedSlots.selection) {
                genSelection = this.genSlotSelection;
            }
            else if (this.hasChips) {
                genSelection = this.genChipSelection;
            }
            else {
                genSelection = this.genCommaSelection;
            }
            while (length--) {
                children[length] = genSelection(this.selectedItems[length], length, length === children.length - 1);
            }
            return this.$createElement('div', {
                staticClass: 'v-select__selections'
            }, children);
        },
        genSlotSelection(item, index) {
            return this.$scopedSlots.selection({
                attrs: {
                    class: 'v-chip--select'
                },
                parent: this,
                item,
                index,
                select: e => {
                    e.stopPropagation();
                    this.selectedIndex = index;
                },
                selected: index === this.selectedIndex,
                disabled: !this.isInteractive
            });
        },
        getMenuIndex() {
            return this.$refs.menu ? this.$refs.menu.listIndex : -1;
        },
        getDisabled(item) {
            return getPropertyFromItem(item, this.itemDisabled, false);
        },
        getText(item) {
            return getPropertyFromItem(item, this.itemText, item);
        },
        getValue(item) {
            return getPropertyFromItem(item, this.itemValue, this.getText(item));
        },
        onBlur(e) {
            e && this.$emit('blur', e);
        },
        onChipInput(item) {
            if (this.multiple)
                this.selectItem(item);
            else
                this.setValue(null);
            if (this.selectedItems.length === 0) {
                this.isMenuActive = true;
            }
            else {
                this.isMenuActive = false;
            }
            this.selectedIndex = -1;
        },
        onClick(e) {
            if (!this.isInteractive)
                return;
            if (!this.isAppendInner(e.target)) {
                this.isMenuActive = true;
            }
            if (!this.isFocused) {
                this.isFocused = true;
                this.$emit('focus');
            }
            this.$emit('click', e);
        },
        onEscDown(e) {
            e.preventDefault();
            if (this.isMenuActive) {
                e.stopPropagation();
                this.isMenuActive = false;
            }
        },
        onKeyPress(e) {
            if (this.multiple || !this.isInteractive || this.disableLookup)
                return;
            const KEYBOARD_LOOKUP_THRESHOLD = 1000;
            const now = performance.now();
            if (now - this.keyboardLookupLastTime > KEYBOARD_LOOKUP_THRESHOLD) {
                this.keyboardLookupPrefix = '';
            }
            this.keyboardLookupPrefix += e.key.toLowerCase();
            this.keyboardLookupLastTime = now;
            const index = this.allItems.findIndex(item => {
                const text = (this.getText(item) || '').toString();
                return text.toLowerCase().startsWith(this.keyboardLookupPrefix);
            });
            const item = this.allItems[index];
            if (index !== -1) {
                this.lastItem = Math.max(this.lastItem, index + 5);
                this.setValue(this.returnObject ? item : this.getValue(item));
                this.$nextTick(() => this.$refs.menu.getTiles());
                setTimeout(() => this.setMenuIndex(index));
            }
        },
        onKeyDown(e) {
            if (this.isReadonly && e.keyCode !== keyCodes.tab)
                return;
            const keyCode = e.keyCode;
            const menu = this.$refs.menu;
            this.$emit('keydown', e);
            if (!menu)
                return;
            if (this.isMenuActive && [keyCodes.up, keyCodes.down, keyCodes.home, keyCodes.end, keyCodes.enter].includes(keyCode)) {
                this.$nextTick(() => {
                    menu.changeListIndex(e);
                    this.$emit('update:list-index', menu.listIndex);
                });
            }
            if ([keyCodes.enter, keyCodes.space].includes(keyCode))
                this.activateMenu();
            if (!this.isMenuActive && [keyCodes.up, keyCodes.down, keyCodes.home, keyCodes.end].includes(keyCode))
                return this.onUpDown(e);
            if (keyCode === keyCodes.esc)
                return this.onEscDown(e);
            if (keyCode === keyCodes.tab)
                return this.onTabDown(e);
            if (keyCode === keyCodes.space)
                return this.onSpaceDown(e);
        },
        onMenuActiveChange(val) {
            if (this.multiple && !val || this.getMenuIndex() > -1)
                return;
            const menu = this.$refs.menu;
            if (!menu || !this.isDirty)
                return;
            this.$refs.menu.getTiles();
            for (let i = 0; i < menu.tiles.length; i++) {
                if (menu.tiles[i].getAttribute('aria-selected') === 'true') {
                    this.setMenuIndex(i);
                    break;
                }
            }
        },
        onMouseUp(e) {
            if (this.hasMouseDown && e.which !== 3 && this.isInteractive) {
                if (this.isAppendInner(e.target)) {
                    this.$nextTick(() => this.isMenuActive = !this.isMenuActive);
                }
            }
            VTextField.options.methods.onMouseUp.call(this, e);
        },
        onScroll() {
            if (!this.isMenuActive) {
                requestAnimationFrame(() => this.getContent().scrollTop = 0);
            }
            else {
                if (this.lastItem > this.computedItems.length)
                    return;
                const showMoreItems = this.getContent().scrollHeight - (this.getContent().scrollTop + this.getContent().clientHeight) < 200;
                if (showMoreItems) {
                    this.lastItem += 20;
                }
            }
        },
        onSpaceDown(e) {
            e.preventDefault();
        },
        onTabDown(e) {
            const menu = this.$refs.menu;
            if (!menu)
                return;
            const activeTile = menu.activeTile;
            if (!this.multiple && activeTile && this.isMenuActive) {
                e.preventDefault();
                e.stopPropagation();
                activeTile.click();
            }
            else {
                this.blur(e);
            }
        },
        onUpDown(e) {
            const menu = this.$refs.menu;
            if (!menu)
                return;
            e.preventDefault();
            if (this.multiple)
                return this.activateMenu();
            const keyCode = e.keyCode;
            menu.isBooted = true;
            window.requestAnimationFrame(() => {
                menu.getTiles();
                if (!menu.hasClickableTiles)
                    return this.activateMenu();
                switch (keyCode) {
                    case keyCodes.up:
                        menu.prevTile();
                        break;
                    case keyCodes.down:
                        menu.nextTile();
                        break;
                    case keyCodes.home:
                        menu.firstTile();
                        break;
                    case keyCodes.end:
                        menu.lastTile();
                        break;
                }
                this.selectItem(this.allItems[this.getMenuIndex()]);
            });
        },
        selectItem(item) {
            if (!this.multiple) {
                this.setValue(this.returnObject ? item : this.getValue(item));
                this.isMenuActive = false;
            }
            else {
                const internalValue = (this.internalValue || []).slice();
                const i = this.findExistingIndex(item);
                i !== -1 ? internalValue.splice(i, 1) : internalValue.push(item);
                this.setValue(internalValue.map(i => {
                    return this.returnObject ? i : this.getValue(i);
                }));
                this.$nextTick(() => {
                    this.$refs.menu && this.$refs.menu.updateDimensions();
                });
                if (this.hideSelected) {
                    this.setMenuIndex(-1);
                }
                else {
                    const index = this.allItems.indexOf(item);
                    if (~index) {
                        this.$nextTick(() => this.$refs.menu.getTiles());
                        setTimeout(() => this.setMenuIndex(index));
                    }
                }
            }
        },
        setMenuIndex(index) {
            this.$refs.menu && (this.$refs.menu.listIndex = index);
        },
        setSelectedItems() {
            const selectedItems = [];
            const values = !this.multiple || !Array.isArray(this.internalValue) ? [this.internalValue] : this.internalValue;
            for (const value of values) {
                const index = this.allItems.findIndex(v => this.valueComparator(this.getValue(v), this.getValue(value)));
                if (index > -1) {
                    selectedItems.push(this.allItems[index]);
                }
            }
            this.selectedItems = selectedItems;
        },
        setValue(value) {
            if (!this.valueComparator(value, this.internalValue)) {
                this.internalValue = value;
                this.$emit('change', value);
            }
        },
        isAppendInner(target) {
            const appendInner = this.$refs['append-inner'];
            return appendInner && (appendInner === target || appendInner.contains(target));
        }
    }
});
